import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useItem, useList } from '../api/queries'
import type { Alert, Dashboard, EquipmentRow, Plant } from '../api/types'
import { BandDonut, BandPills, HBarList, HatchDefs, StackedAreaBars, TrendLine } from '../components/charts'
import { DataTable, type Column } from '../components/DataTable'
import { DateRangeFilter } from '../components/DateRangeFilter'
import { Icon } from '../components/Icon'
import { PlantMap } from '../components/PlantMap'
import { BandBadge, Breadcrumbs, Card, EmptyState, ErrorState, KpiCard, LoadingState, PageHeader, RouteCriticalBadge, Segmented, StatusBadge } from '../components/ui'
import { areaPath, overviewPath, plantAssetsPath, plantWorkOrdersPath } from '../features/dashboard/drilldown'
import {
  avgScoreBars, bandDonut, closeTimeRows, filtersToParams, groupTrend,
  kpiCards, stackedByArea, trendSeries, type DashboardFilters, type TrendGrain,
} from '../features/dashboard/models'
import { useDashboardFilters } from '../features/dashboard/useDashboardFilters'
import { areaLabel } from '../lib/areas'
import { bandMeta } from '../lib/bands'
import { formatDate, humanize } from '../lib/format'

const SEVERITY_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 }

function equipmentColumns(): Column<EquipmentRow>[] {
  return [
    { key: 'name', header: 'Equipo', cell: (r) => <><strong>{r.name}</strong><div className="muted mono">{r.code}</div></> },
    { key: 'area', header: 'Área', cell: (r) => areaLabel(undefined, r.area_name), priority: 'low' },
    { key: 'score', header: 'Puntaje', cell: (r) => <BandBadge band={r.band} total={r.total} size="sm" />, align: 'right' },
    { key: 'date', header: 'Inspeccionado', cell: (r) => formatDate(r.inspected_on), priority: 'low' },
  ]
}

function AlertsPanel({ asOf, plantId }: { asOf?: string; plantId: number }) {
  const alerts = useList<Alert>('/api/alerts/', { status: 'open', page_size: 50, as_of: asOf, plant: plantId })
  const rows = [...(alerts.data?.results ?? [])].sort((a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9)).slice(0, 6)
  const allAlertsPath = `/alerts?plant=${plantId}${asOf ? `&as_of=${asOf}` : ''}`
  return (
    <Card title="Alertas" actions={<Link className="link-arrow" to={allAlertsPath}>Ver todo <Icon name="arrow-up-right" size={14} /></Link>}>
      {asOf ? <p className="faint small">Alertas al {formatDate(asOf)}: se omiten los datos posteriores.</p> : null}
      {alerts.isPending ? <LoadingState label="Cargando alertas…" /> : alerts.isError ? <ErrorState error={alerts.error} onRetry={() => void alerts.refetch()} /> : rows.length === 0 ? <EmptyState title="Sin alertas abiertas" /> : (
        <ul className="alert-list">
          {rows.map((a) => (
            <li key={a.id} className="alert-list__item" data-severity={a.severity}>
              <StatusBadge label={humanize(a.severity)} tone={a.severity === 'critical' ? 'danger' : a.severity === 'warning' ? 'warn' : 'info'} icon={a.severity === 'info' ? 'info' : 'triangle-alert'} />
              <span>{a.message}{a.equipment_code ? <> <span className="muted mono">{a.equipment_code}</span></> : null}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function Widgets({ data, filters, plantId }: { data: Dashboard; filters: DashboardFilters; plantId: number }) {
  const navigate = useNavigate()
  const [barMode, setBarMode] = useState<'count' | 'share'>('count')
  const [bandFilter, setBandFilter] = useState('all')
  const [grain, setGrain] = useState<TrendGrain>('month')
  const kpis = kpiCards(data.kpis)
  const donut = bandDonut(data.band_distribution)
  const stacked = stackedByArea(data.by_area)
  const trend = trendSeries(data.score_trend)
  const trendPoints = groupTrend(trend.points, grain)
  const scoreBars = avgScoreBars(data.by_area)
  const closing = closeTimeRows(data.wo_close_time_by_area)
  const bandCounts = Object.fromEntries(data.band_distribution.map((d) => [d.band, d.count]))
  const cols = equipmentColumns()
  const toAsset = (r: EquipmentRow) => navigate(`/assets/${r.equipment}`)
  const openArea = (area?: number) => { if (area) navigate(areaPath(plantId, area, filters)) }
  const assetsPath = (band?: DashboardFilters['band']) => plantAssetsPath(plantId, filters, band)
  const plantPath = (path: string, withDates = false) => {
    const params = new URLSearchParams({ plant: String(plantId) })
    if (withDates && filters.dateFrom) params.set('from', filters.dateFrom)
    if (withDates && filters.dateTo) params.set('to', filters.dateTo)
    return `${path}?${params.toString()}`
  }

  return (
    <>
      <HatchDefs />

      <section className="kpi-row" aria-label="Cifras clave">
        {kpis.map((k) => (
          <KpiCard key={k.key} label={k.label} value={k.value} caption={k.caption} tone={k.tone}
            onClick={() => navigate(k.key === 'work_orders' ? plantWorkOrdersPath(plantId) : assetsPath(k.band))} />
        ))}
      </section>

      <div className="grid grid--2">
        <Card title="Equipos por área" actions={<Segmented small label="Mostrar" value={barMode} onChange={setBarMode} options={[{ value: 'count', label: 'Cantidad' }, { value: 'share', label: 'Proporción' }]} />}
          footer={<BandPills value={bandFilter} onChange={setBandFilter} counts={bandCounts} />}>
          <StackedAreaBars rows={stacked} mode={barMode} bandFilter={bandFilter} onSelectArea={openArea} />
        </Card>
        <Card title="Tendencia del puntaje promedio" actions={<Segmented small label="Agrupar por" value={grain} onChange={setGrain} options={[{ value: 'month', label: 'Mes' }, { value: 'quarter', label: 'Trimestre' }, { value: 'year', label: 'Año' }]} />}
          footer={trend.sparse ? <p className="sparse-note"><Icon name="info" size={14} /> {trend.note || 'Historial escaso: una tendencia necesita varios periodos antes de indicar una dirección.'}</p> : undefined}>
          <TrendLine points={trendPoints} highlight={{ from: filters.dateFrom, to: filters.dateTo }} />
        </Card>
      </div>

      <div className="grid grid--3">
        <Card title="Proporción por banda"><BandDonut model={donut} onSelect={(band) => navigate(assetsPath(band as DashboardFilters['band']))} /></Card>
        <Card title="Puntaje promedio por área" actions={<Link className="link-arrow" to={assetsPath()}>Ver todo <Icon name="arrow-up-right" size={14} /></Link>}>
          <HBarList items={scoreBars.map((b) => ({ key: b.area, label: areaLabel(b.code, b.name), value: b.label, widthPct: b.widthPct, tone: bandMeta(b.worstBand).color }))} />
        </Card>
        <Card title="Días para cerrar órdenes de trabajo" footer={<div className="pills" aria-label="Resumen de cierres"><StatusBadge tone="ok" label="A tiempo" /><StatusBadge tone="warn" label="Con retraso" /><StatusBadge tone="danger" label="Con mucho retraso" /></div>}>
          <HBarList empty="Aún no hay órdenes de trabajo cerradas" items={closing.map((c) => ({
            key: c.area, label: areaLabel(c.code, c.name), value: c.label, widthPct: c.widthPct, tone: 'var(--brand-mid)',
            extra: c.closed ? <span className="mini-pills mono" aria-label={`${c.pills.on_time} a tiempo, ${c.pills.late} con retraso, ${c.pills.too_late} con mucho retraso`}><span data-tone="ok">{c.pills.on_time}</span><span data-tone="warn">{c.pills.late}</span><span data-tone="danger">{c.pills.too_late}</span></span> : null,
          }))} />
        </Card>
      </div>

      <Card title="Mapa de planta">
        <p className="faint small">Elegir un área para ver sus equipos.</p>
        <PlantMap tiles={data.plant_map} onSelect={openArea} />
      </Card>

      <div className="grid grid--2">
        <Card title="Equipos críticos principales" actions={<Link className="link-arrow" to={assetsPath('critical')}>Ver todo <Icon name="arrow-up-right" size={14} /></Link>}>
          <DataTable caption="Equipos críticos principales" columns={cols} rows={data.top_critical} rowKey={(r) => r.equipment} onRowClick={toAsset} rowLabel={(r) => `Abrir ${r.name}`} emptyTitle="Sin equipos críticos" />
        </Card>
        <Card title="Equipos de ruta crítica" actions={<RouteCriticalBadge />}>
          <DataTable caption="Equipos de ruta crítica" columns={cols} rows={data.route_critical} rowKey={(r) => r.equipment} onRowClick={toAsset} rowLabel={(r) => `Abrir ${r.name}`} emptyTitle="Sin equipos de ruta crítica" />
        </Card>
      </div>

      <div className="grid grid--4">
        <AlertsPanel asOf={filters.dateTo} plantId={plantId} />
        <Card title="Resumen de cargas" actions={<Link className="link-arrow" to={plantPath('/measurements', true)}>Abrir <Icon name="arrow-up-right" size={14} /></Link>}>
          <ul className="stat-list">
            <li><StatusBadge tone="off" label="Apagado" icon="power" /><b className="mono">{data.loads.off}</b></li>
            <li><StatusBadge tone="neutral" label="Sin medir" icon="info" /><b className="mono">{data.loads.not_measured}</b></li>
            <li><StatusBadge tone="danger" label="Requiere nueva medición" icon="triangle-alert" /><b className="mono">{data.loads.needs_remeasure}</b></li>
            <li><span>Medidos</span><b className="mono">{data.loads.measured}</b></li>
            <li><span>Sin medición</span><b className="mono">{data.loads.without_measurement}</b></li>
          </ul>
        </Card>
        <Card title="Lubricación" actions={<Link className="link-arrow" to={plantPath('/lubrication')}>Abrir <Icon name="arrow-up-right" size={14} /></Link>}>
          <ul className="stat-list">
            <li><span>Cambios de aceite recomendados</span><b className="mono">{data.lubrication.recommended_changes} de {data.lubrication.plan_changes_total}</b></li>
            <li><span>Revisiones de nivel recomendadas</span><b className="mono">{data.lubrication.recommended_checks} de {data.lubrication.plan_checks_total}</b></li>
            <li><span>Próximos cambios por vencer</span><b className="mono">{data.lubrication.due}</b></li>
            <li><span>Fecha de vencimiento desconocida</span><b className="mono">{data.lubrication.unknown}</b></li>
          </ul>
          {data.lubrication.plan_changes_total > data.lubrication.recommended_changes ? <p className="faint small">{data.lubrication.plan_changes_total - data.lubrication.recommended_changes} nombre(s) de cambio de aceite del informe sin asociar: ver la página de Lubricación.</p> : null}
          <p className="faint small">Desconocido significa que faltan las horas de operación o el tipo de aceite; no se supone ningún valor.</p>
        </Card>
        <Card title="Calidad de datos" actions={<Link className="link-arrow" to={plantPath('/data-quality')}>Abrir <Icon name="arrow-up-right" size={14} /></Link>}>
          <div className="big-number"><span className="mono">{data.data_quality.needs_attention}</span><span className="muted">errores y advertencias</span></div>
          <p className="muted">{data.data_quality.by_severity.error ?? 0} {(data.data_quality.by_severity.error ?? 0) === 1 ? 'error' : 'errores'} · {data.data_quality.by_severity.warning ?? 0} {(data.data_quality.by_severity.warning ?? 0) === 1 ? 'advertencia' : 'advertencias'}</p>
          <p className="faint small">{data.data_quality.by_severity.info ?? 0} elementos informativos se conservan en la página de Calidad de datos.</p>
        </Card>
      </div>
    </>
  )
}

/** Plant level of the dashboard: one plant by area. Selecting an area opens its equipment. */
export function DashboardPage() {
  const plantId = Number(useParams().plantId)
  const { filters, set } = useDashboardFilters()
  const navigate = useNavigate()
  const plant = useItem<Plant>(`/api/plants/${plantId}/`)
  const dash = useItem<Dashboard>('/api/dashboard/', { ...filtersToParams({ dateFrom: filters.dateFrom, dateTo: filters.dateTo }), plant: plantId })
  const plantName = plant.data?.name ?? 'Planta'

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Panel', to: overviewPath(filters) }, { label: plantName }]} />
      <PageHeader
        title={plantName}
        subtitle={filters.dateTo ? `Equipos por área: inspección más reciente de cada equipo hasta el ${formatDate(filters.dateTo)}` : 'Equipos por área: inspección más reciente de cada equipo'}
        actions={
          <div className="filters">
            <label className="select-pill">Abrir área
              <select aria-label="Abrir área" value="" disabled={!dash.data} onChange={(e) => { if (e.target.value) navigate(areaPath(plantId, Number(e.target.value), filters)) }}>
                <option value="">Elegir área…</option>
                {dash.data?.by_area.filter((a) => a.plant === plantId).map((a) => <option key={a.area} value={a.area}>{areaLabel(a.code, a.name)}</option>)}
              </select>
            </label>
            <DateRangeFilter value={{ from: filters.dateFrom, to: filters.dateTo }} onChange={(r) => set({ ...filters, dateFrom: r.from, dateTo: r.to })} />
          </div>
        }
      />
      {dash.isPending ? <LoadingState label="Cargando el panel…" /> : dash.isError ? <ErrorState error={dash.error} onRetry={() => void dash.refetch()} title="No se pudo cargar el panel" /> : <Widgets data={dash.data} filters={filters} plantId={plantId} />}
    </div>
  )
}
